import { useState } from 'react'
import type { ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import {
  TbCircle,
  TbCircleCheck,
  TbEye,
  TbEyeClosed,
  TbLoader2,
  TbLockPassword,
  TbLogout,
  TbMail
} from 'react-icons/tb'

import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { z } from 'zod'

import type { TProfileResponse } from '@/api/auth/types'

import { api } from '@/api'
import { passwordChecks, passwordRuleMessage } from '@/api/auth/password-reset'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { useAuth } from '@/hooks/use-auth'
import { cn } from '@/lib/utils'
import { requiredString } from '@/types/common'

const PASSWORD_RULES_ID = 'first-login-password-rules'
const EMAIL_NOTE_ID = 'first-login-email-note'

const firstLoginPasswordSchema = z
  .object({
    email: requiredString.email('Email tidak valid'),
    current_password: requiredString,
    new_password: requiredString,
    new_password_confirmation: requiredString,
    confirmed: z.boolean().refine(value => value, {
      message:
        'Centang konfirmasi email dan penyimpanan password terlebih dahulu.'
    })
  })
  .superRefine((data, ctx) => {
    const message = passwordRuleMessage(data.new_password)
    if (message) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
        path: ['new_password']
      })
    }

    if (
      data.new_password_confirmation.length > 0 &&
      data.new_password !== data.new_password_confirmation
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Konfirmasi password tidak sesuai.',
        path: ['new_password_confirmation']
      })
    }
  })

type FirstLoginPasswordValues = z.infer<typeof firstLoginPasswordSchema>

interface DialogChangePasswordProps {
  open: boolean
  profile: TProfileResponse
  onCompleted: (email: string, passwordChangedAt: string) => void
}

function Rule({ ok, children }: { ok: boolean; children: ReactNode }) {
  return (
    <li
      className={cn(
        'flex items-center gap-2 text-xs transition-colors',
        ok ? 'text-primary' : 'text-gray-500'
      )}
    >
      {ok ? (
        <TbCircleCheck className="size-4" />
      ) : (
        <TbCircle className="size-4" />
      )}
      {children}
    </li>
  )
}

function VisibilityToggle({
  shown,
  label,
  onToggle
}: {
  shown: boolean
  label: string
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      aria-label={shown ? `Sembunyikan ${label}` : `Tampilkan ${label}`}
      onClick={onToggle}
      className="hover:bg-primary/10 cursor-pointer rounded-full p-1 duration-300 focus:outline-none"
    >
      {shown ? (
        <TbEye className="text-primary size-5" />
      ) : (
        <TbEyeClosed className="text-primary size-5" />
      )}
    </button>
  )
}

export default function DialogChangePassword({
  open,
  profile,
  onCompleted
}: DialogChangePasswordProps) {
  const { signOut, signOutState } = useAuth()
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const form = useForm<FirstLoginPasswordValues>({
    resolver: zodResolver(firstLoginPasswordSchema),
    defaultValues: {
      email: profile.email ?? '',
      current_password: '',
      new_password: '',
      new_password_confirmation: '',
      confirmed: false
    }
  })

  const newPassword = form.watch('new_password')
  const passwordConfirmation = form.watch('new_password_confirmation')
  const confirmed = form.watch('confirmed')
  const passwordRules = passwordChecks(newPassword, passwordConfirmation)

  const changePassword = useMutation(api.profile.resetPassword())
  const pending = changePassword.isPending || signOutState.isPending

  const onSubmit = async (data: FirstLoginPasswordValues) => {
    const email = data.email.trim()

    try {
      const response = await changePassword.mutateAsync({
        email,
        current_password: data.current_password,
        new_password: data.new_password,
        new_password_confirmation: data.new_password_confirmation,
        confirmed: data.confirmed
      })

      toast.success(
        response.message || 'Email dan password berhasil diperbarui'
      )
      onCompleted(
        email,
        response.data?.password_changed_at || new Date().toISOString()
      )
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Gagal menyimpan perubahan'
      toast.error(message)
    }
  }

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="my-0 max-h-[min(760px,calc(100dvh-2rem))] gap-5 overflow-y-auto rounded-2xl p-6 sm:max-w-3xl"
        centered
        isHideCloseButton
        onEscapeKeyDown={event => event.preventDefault()}
        onPointerDownOutside={event => event.preventDefault()}
      >
        <div className="grid justify-items-center gap-3 text-center">
          <div className="bg-primary/10 text-primary ring-primary/5 grid size-12 place-items-center rounded-2xl ring-8">
            <TbLockPassword className="size-7" />
          </div>
          <DialogHeader className="items-center gap-1.5 sm:text-center">
            <DialogTitle className="text-2xl font-semibold tracking-tight text-gray-900">
              Ubah Password
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-gray-500">
              Periksa email Anda, lalu ubah password sebelum melanjutkan.
            </DialogDescription>
          </DialogHeader>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="grid gap-4"
            noValidate
          >
            <p
              id={EMAIL_NOTE_ID}
              className="rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm leading-relaxed text-amber-900"
            >
              Jika email tidak sesuai, ubah email di bawah. Pastikan email
              tersebut aktif dan valid.
            </p>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Email</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        placeholder="Masukkan email"
                        prefixIcon={<TbMail className="text-primary size-5" />}
                        className="w-full rounded-xl"
                        disabled={pending}
                        aria-describedby={EMAIL_NOTE_ID}
                        autoFocus={!profile.email}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="current_password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Password lama</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type={showCurrentPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        placeholder="Masukkan password lama"
                        prefixIcon={
                          <TbLockPassword className="text-primary size-5" />
                        }
                        suffixIcon={
                          <VisibilityToggle
                            shown={showCurrentPassword}
                            label="password lama"
                            onToggle={() =>
                              setShowCurrentPassword(current => !current)
                            }
                          />
                        }
                        className="w-full rounded-xl"
                        disabled={pending}
                        autoFocus={!!profile.email}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Separator className="bg-zinc-200 sm:col-span-2" />

              <FormField
                control={form.control}
                name="new_password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Password baru</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type={showNewPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        placeholder="Masukkan password baru"
                        prefixIcon={
                          <TbLockPassword className="text-primary size-5" />
                        }
                        suffixIcon={
                          <VisibilityToggle
                            shown={showNewPassword}
                            label="password baru"
                            onToggle={() =>
                              setShowNewPassword(current => !current)
                            }
                          />
                        }
                        className="w-full rounded-xl"
                        disabled={pending}
                        aria-describedby={PASSWORD_RULES_ID}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="new_password_confirmation"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Konfirmasi password</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        placeholder="Ulangi password baru"
                        prefixIcon={
                          <TbLockPassword className="text-primary size-5" />
                        }
                        suffixIcon={
                          <VisibilityToggle
                            shown={showConfirmPassword}
                            label="konfirmasi password"
                            onToggle={() =>
                              setShowConfirmPassword(current => !current)
                            }
                          />
                        }
                        className="w-full rounded-xl"
                        disabled={pending}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <ul
              id={PASSWORD_RULES_ID}
              className="grid gap-x-4 gap-y-1.5 sm:grid-cols-2"
              aria-label="Syarat password"
            >
              <Rule ok={passwordRules.length}>Minimal 8 karakter</Rule>
              <Rule ok={passwordRules.letterAndNumber}>
                Kombinasi huruf dan angka
              </Rule>
              <Rule ok={passwordRules.uppercase}>Ada huruf besar</Rule>
              <Rule ok={passwordRules.special}>
                Ada karakter khusus, misalnya ! @ #
              </Rule>
              <Rule ok={passwordRules.match}>
                Konfirmasi sama dengan password
              </Rule>
            </ul>

            <FormField
              control={form.control}
              name="confirmed"
              render={({ field }) => (
                <FormItem className="gap-2">
                  <div className="flex items-start gap-3">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={checked =>
                          field.onChange(checked === true)
                        }
                        disabled={pending}
                        className="mt-0.5"
                      />
                    </FormControl>
                    <FormLabel className="cursor-pointer text-sm leading-relaxed font-normal text-gray-700">
                      Saya memastikan email sudah valid dan benar, serta
                      password baru sudah saya catat di tempat yang aman.
                    </FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-2 sm:grid-cols-2">
            <Button
                type="button"
                variant="outline"
                size="lg"
                disabled={pending}
                onClick={() => signOut()}
                className="h-12 w-full rounded-xl text-base"
              >
                {signOutState.isPending ? (
                  <>
                    <TbLoader2 className="size-5 animate-spin motion-reduce:animate-none" />
                    Keluar...
                  </>
                ) : (
                  <>
                    <TbLogout className="size-5" />
                    Keluar
                  </>
                )}
              </Button>
              
              <Button
                type="submit"
                size="lg"
                disabled={pending || !confirmed}
                className="shadow-primary/25 h-12 w-full rounded-xl text-base shadow-md"
              >
                {changePassword.isPending ? (
                  <>
                    <TbLoader2 className="size-5 animate-spin motion-reduce:animate-none" />
                    Menyimpan...
                  </>
                ) : (
                  'Simpan'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
